// CityMap.js — NeevVerse city map replacing the home screen grid
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import WorldCard from './WorldCard';
import FriendFeed from '../Social/FriendFeed';
import { supabase } from '../../supabase';

const css = `
@keyframes nv-botWalk {
  0%   { left: -60px; }
  100% { left: calc(100% + 20px); }
}
@keyframes nv-botBob {
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-4px); }
}
@keyframes nv-gridPulse {
  0%,100% { opacity: 0.04; }
  50%      { opacity: 0.08; }
}
@keyframes nv-sectionGlow {
  0%,100% { opacity: 0.6; }
  50%      { opacity: 1; }
}
.nv-bot-walker {
  position: absolute;
  bottom: 8px;
  animation: nv-botWalk 18s linear infinite;
  z-index: 4;
  pointer-events: none;
}
.nv-bot-bob {
  animation: nv-botBob 0.7s ease-in-out infinite;
  display: inline-block;
  font-size: 22px;
}
.nv-grid-bg {
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(255,214,0,0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,214,0,0.08) 1px, transparent 1px);
  background-size: 40px 40px;
  animation: nv-gridPulse 4s ease-in-out infinite;
  pointer-events: none;
}
.nv-section-title {
  animation: nv-sectionGlow 3s ease-in-out infinite;
}
`;

// World definitions — maps to existing screen IDs
export const WORLDS = [
  // Row 1 — Academics
  { id: 'olympiad',        name: 'Champions\nAcademy',      icon: '🎓', color: '#8B2BE2', zone: 'Academy' },
  // { id: 'subject_practice',name: 'CBSE\nArena', icon: '📖', color: '#1565C0', zone: 'Academy' }, // hidden — no content yet
  { id: 'mocktest',        name: 'Test\nColosseum',          icon: '📝', color: '#D32F2F', zone: 'Academy' },
  { id: 'brain',           name: 'Mind\nMaze',               icon: '🧩', color: '#0066FF', zone: 'Academy' },
  // Row 2 — Science & Tech
  { id: 'science',         name: 'Science &\nSTEM World',     icon: '🔬', color: '#00897B', zone: 'Science' },
  { id: 'ai_tutor',        name: 'AI\nLab',                  icon: '🤖', color: '#B44FFF', zone: 'Science' },
  { id: 'maths',           name: 'Maths\nTemple',            icon: '➗', color: '#FF9500', zone: 'Science' },
  // Row 3 — Life Skills
  { id: 'entrepreneurship',name: 'Startup\nGarage',          icon: '🏗️', color: '#FF6B00', zone: 'Life Skills' },
  { id: 'money',           name: 'Money\nCity',              icon: '💰', color: '#00C851', zone: 'Life Skills' },
  { id: 'language',        name: 'Language\nKingdom',        icon: '🦜', color: '#E91E8C', zone: 'Life Skills' },
  { id: 'homework',        name: 'Homework\nHQ',             icon: '📚', color: '#F57F17', zone: 'Life Skills' },
  // Row 4 — Explore
  { id: 'news',            name: 'News\nTower',              icon: '📰', color: '#0277BD', zone: 'Explore' },
  { id: 'stories',         name: 'Hall of\nLegends',         icon: '⭐', color: '#FFD600', zone: 'Explore' },
  { id: 'challenges',      name: 'Innovation\nIsland',       icon: '💡', color: '#00ACC1', zone: 'Explore' },
  { id: 'games',           name: 'Game\nZone',               icon: '🎮', color: '#FF2D55', zone: 'Explore' },
  // Row 5 — Arena
  { id: 'neevpet',         name: 'NeevPet\nIsland',           icon: '🐾', color: '#FF6B00', zone: 'Arena' },
  { id: 'quiz',            name: 'Quiz\nWorld',              icon: '🏆', color: '#7B1FA2', zone: 'Arena' },
  { id: 'multiplayer',     name: 'Battle\nArena',            icon: '⚔️', color: '#C62828', zone: 'Arena' },
  { id: 'creator_studio',  name: 'Creator\nStudio',          icon: '🎬', color: '#2E7D32', zone: 'Arena' },
];

const ZONES = ['Academy', 'Science', 'Life Skills', 'Explore', 'Arena'];

const ZONE_COLORS = {
  'Academy':    '#FF6B00',
  'Science':    '#00BCD4',
  'Life Skills':'#00C851',
  'Explore':    '#FFD600',
  'Arena':      '#1A1A6E',
};

const ZONE_ICONS = {
  'Academy':    '🎓',
  'Science':    '🔬',
  'Life Skills':'💼',
  'Explore':    '🗺️',
  'Arena':      '⚔️',
};

export default function CityMap({ onNavigate, profile, user }) {
  const [expandedZone, setExpandedZone] = useState(null); // null = all districts collapsed
  const [cityTab, setCityTab] = useState('worlds'); // 'worlds' | 'friends'

  useEffect(() => {
    if (!document.getElementById('nv-css')) {
      const s = document.createElement('style');
      s.id = 'nv-css';
      s.textContent = css;
      document.head.appendChild(s);
    }
  }, []);

  // Auto-generate username if profile doesn't have one
  useEffect(() => {
    if (!profile?.parent_email || profile?.neev_username) return;
    const digits = Math.floor(1000 + Math.random() * 9000);
    const username = `NeevExplorer_${digits}`;
    supabase.from('bm_child_profiles')
      .update({ neev_username: username })
      .eq('parent_email', profile.parent_email)
      .then(() => {}).catch(() => {});
  }, [profile?.parent_email, profile?.neev_username]);

  return (
    <div style={{
      background: 'linear-gradient(180deg, #0D0D3D 0%, #1A1A6E 50%, #0D0D3D 100%)',
      position: 'relative',
      paddingBottom: 32,
      fontFamily: "'Nunito', sans-serif",
    }}>
      {/* Blueprint grid background */}
      <div className="nv-grid-bg" />

      {/* Walking NeevBot */}
      <div style={{ position: 'relative', height: 0, overflow: 'visible' }}>
        <div className="nv-bot-walker">
          <div className="nv-bot-bob">🤖</div>
        </div>
      </div>

      {/* City / Friends tab switcher */}
      <div style={{ padding: '12px 16px 0', position: 'relative', zIndex: 2, display: 'flex', gap: 8 }}>
        {[['worlds','🏙️ NeevVerse'],['friends','👥 Friends']].map(([id, label]) => (
          <button key={id} onClick={() => setCityTab(id)} style={{
            flex: 1, padding: '8px', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: cityTab === id ? 'rgba(255,214,0,0.2)' : 'rgba(255,255,255,0.05)',
            color: cityTab === id ? '#FFD600' : 'rgba(255,255,255,0.4)',
            fontWeight: 800, fontSize: 12, fontFamily: "'Nunito', sans-serif",
            border: cityTab === id ? '1px solid rgba(255,214,0,0.4)' : '1px solid rgba(255,255,255,0.08)',
          }}>{label}</button>
        ))}
      </div>

      {/* Friends panel */}
      {cityTab === 'friends' && (
        <div style={{ background: '#FAFAFA', minHeight: 300, borderRadius: 16, margin: '10px 16px' }}>
          <FriendFeed user={user} profile={profile} />
        </div>
      )}

      {/* City header */}
      <div style={{
        padding: '16px 16px 10px',
        position: 'relative', zIndex: 2,
        display: cityTab === 'worlds' ? 'block' : 'none',
      }}>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: 'linear-gradient(135deg, rgba(255,214,0,0.12), rgba(255,150,0,0.08))',
            border: '1px solid rgba(255,214,0,0.25)',
            borderRadius: 16, padding: '10px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <div>
            <p style={{ margin: 0, fontSize: 10, fontWeight: 900, color: 'rgba(255,214,0,0.7)', letterSpacing: '0.08em' }}>
              WELCOME TO
            </p>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: 'white' }}>
              🌟 NeevVerse
            </h2>
            <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 700 }}>
              {profile?.child_name ? `${profile.child_name}'s city` : 'Your learning city'}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ margin: 0, fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 700 }}>WORLDS</p>
            <p style={{ margin: 0, fontSize: 24, fontWeight: 900, color: '#FFD600' }}>{WORLDS.length}</p>
          </div>
        </motion.div>
      </div>

      {/* District accordion — only in worlds tab */}
      <div style={{ padding: '0 14px', position: 'relative', zIndex: 2, display: cityTab === 'worlds' ? 'block' : 'none' }}>
        {ZONES.map(zone => {
          const zoneWorlds = WORLDS.filter(w => w.zone === zone);
          if (zoneWorlds.length === 0) return null;
          const zColor = ZONE_COLORS[zone];
          const isOpen = expandedZone === zone;
          return (
            <div key={zone} style={{ marginBottom: 10 }}>
              {/* District tile — tap to expand/collapse */}
              <button
                onClick={() => setExpandedZone(isOpen ? null : zone)}
                style={{
                  width: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex', alignItems: 'center', gap: 12,
                  background: isOpen
                    ? `linear-gradient(135deg, ${zColor}3d 0%, ${zColor}14 100%)`
                    : `linear-gradient(135deg, ${zColor}22 0%, rgba(255,255,255,0.04) 100%)`,
                  border: `1px solid ${isOpen ? `${zColor}88` : `${zColor}3d`}`,
                  borderRadius: 16,
                  padding: '14px 16px',
                  cursor: 'pointer',
                  fontFamily: "'Nunito', sans-serif",
                  transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
                  boxShadow: isOpen ? `0 8px 22px ${zColor}40` : `0 2px 10px ${zColor}1a`,
                }}
              >
                {/* Decorative skyline — the district's own world icons, faded, as a background collage */}
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
                  gap: 2, paddingRight: 8, opacity: 0.16,
                  pointerEvents: 'none', overflow: 'hidden',
                }}>
                  {zoneWorlds.map((w) => (
                    <span key={w.id} style={{ fontSize: 36, lineHeight: 1 }}>{w.icon}</span>
                  ))}
                </div>
                {/* Soft radial glow behind the badge */}
                <div style={{
                  position: 'absolute', left: -10, top: '50%', transform: 'translateY(-50%)',
                  width: 90, height: 90, borderRadius: '50%',
                  background: `radial-gradient(circle, ${zColor}55 0%, transparent 70%)`,
                  pointerEvents: 'none',
                }} />

                {/* Icon badge */}
                <div style={{
                  width: 52, height: 52, borderRadius: 15, flexShrink: 0,
                  position: 'relative', zIndex: 1,
                  background: `linear-gradient(145deg, ${zColor} 0%, ${zColor}AA 100%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 27,
                  boxShadow: `0 4px 14px ${zColor}77, inset 0 1px 3px rgba(255,255,255,0.35)`,
                }}>
                  {ZONE_ICONS[zone] || '🏙️'}
                </div>

                <div style={{ flex: 1, textAlign: 'left', position: 'relative', zIndex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: 'white' }}>{zone} District</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)' }}>
                    {zoneWorlds.length} world{zoneWorlds.length === 1 ? '' : 's'}
                  </div>
                </div>
                <motion.span
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ fontSize: 14, color: zColor, fontWeight: 900, position: 'relative', zIndex: 1 }}
                >
                  ▼
                </motion.span>
              </button>

              {/* World cards — expands inline when this district is open */}
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: 8,
                      marginTop: 8,
                      paddingBottom: 4,
                    }}>
                      {zoneWorlds.map((world, i) => (
                        <WorldCard
                          key={world.id}
                          world={world}
                          index={i}
                          onEnter={onNavigate}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* City footer */}
      <div style={{
        textAlign: 'center', padding: '8px 16px 0',
        position: 'relative', zIndex: 2,
      }}>
        <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', fontWeight: 700, margin: 0 }}>
          🌐 NeevVerse — {WORLDS.length} Worlds • Classes 3–8
        </p>
      </div>
    </div>
  );
}
